import { BaseFieldDefinition, FieldType } from '../../../core/field-definitions/entities/field-definition.entity';
import { ThesaurusConceptReference } from '../../../core/thesaurus/entities/thesaurus-concept.entity';
import { TypedValue } from '../../../form-controls/components/form-typed-value-list/typed-values.entity';
import { FormValue, FormValueState } from '../../../form-controls/entities/form-value.entity';

type MergedFieldValueFunction = (def: BaseFieldDefinition) => FormValue<any> | FormValue<any>[];

/**
 * For easier detection and to decrease cases where we accidentally overwrite values we use Indeterminate values as initial value
 * This is only the case for 'single' values
 * This clarifies cases where we want to specifically overwrite a field and visually shows if a value was selected or not
 */
export class BatchFieldReadHelper {
    private static readonly FieldMapping: Record<FieldType, MergedFieldValueFunction> = {
        [FieldType.SimpleField]: this.getBatchSimpleValue,
        [FieldType.TextField]: this.getBatchSimpleValue,
        [FieldType.HiddenField]: this.getBatchUnsupportedValue,
        [FieldType.BooleanField]: this.getBatchSimpleValue,
        [FieldType.EnumField]: this.getBatchEnumValue,
        [FieldType.LongField]: this.getBatchSimpleValue,
        [FieldType.FramesField]: this.getBatchSimpleValue,
        [FieldType.DoubleField]: this.getBatchSimpleValue,
        [FieldType.TimeCodeField]: this.getBatchSimpleValue,
        [FieldType.DateField]: this.getBatchSimpleValue,
        [FieldType.EDTFField]: this.getBatchSimpleValue,
        [FieldType.TimerField]: this.getBatchComplexValue,
        [FieldType.ListField]: this.getBatchListValue,
        [FieldType.MultiItemField]: this.getBatchListValue,
        [FieldType.PositionField]: this.getBatchPositionValue,
        [FieldType.GeoCoordinateField]: this.getBatchUnsupportedValue,
        [FieldType.UrlField]: this.getBatchSimpleValue,
        [FieldType.VectorField]: this.getBatchSimpleValue,
        [FieldType.MapField]: this.getBatchComplexValue,
        [FieldType.ThesaurusField]: this.getBatchThesaurusValue,
        [FieldType.ThesaurusUriField]: this.getBatchUnsupportedValue,
        [FieldType.ThesaurusLabelField]: this.getBatchUnsupportedValue,
        [FieldType.EnumMapField]: this.getBatchUnsupportedValue,
        [FieldType.ComplexField]: this.getBatchComplexValue,
        [FieldType.ComplexListField]: this.getBatchListValue,
        [FieldType.ComplexMapField]: this.getBatchComplexValue,
        [FieldType.OrderedMultiItemField]: this.getBatchUnsupportedValue,
        [FieldType.FieldDefinitionReferenceField]: this.getBatchSimpleValue
    };

    private static readonly EmptyValueMapping: Record<FieldType, FormValue<any> | FormValue<any>[]> = {
        [FieldType.SimpleField]: new FormValue(null),
        [FieldType.TextField]: new FormValue(null),
        [FieldType.HiddenField]: new FormValue(null),
        [FieldType.BooleanField]: new FormValue(null),
        [FieldType.EnumField]: [],
        [FieldType.LongField]: new FormValue(null),
        [FieldType.FramesField]: new FormValue(null),
        [FieldType.DoubleField]: new FormValue(null),
        [FieldType.TimeCodeField]: new FormValue(null),
        [FieldType.DateField]: new FormValue(null),
        [FieldType.EDTFField]: new FormValue(null),
        [FieldType.TimerField]: new FormValue(null),
        [FieldType.ListField]: [],
        [FieldType.MultiItemField]: [],
        [FieldType.PositionField]: [
            new FormValue({
                type: 'Address',
                value: ''
            }),
            new FormValue({
                type: 'Location',
                value: ''
            })
        ],
        [FieldType.GeoCoordinateField]: new FormValue(null),
        [FieldType.UrlField]: new FormValue(null),
        [FieldType.VectorField]: new FormValue(null),
        [FieldType.MapField]: new FormValue({}),
        [FieldType.ThesaurusField]: [],
        [FieldType.ThesaurusUriField]: new FormValue(null),
        [FieldType.ThesaurusLabelField]: new FormValue(null),
        [FieldType.EnumMapField]: [],
        [FieldType.ComplexField]: new FormValue({}),
        [FieldType.ComplexListField]: [],
        [FieldType.ComplexMapField]: new FormValue({}),
        [FieldType.OrderedMultiItemField]: [],
        [FieldType.FieldDefinitionReferenceField]: new FormValue(null)
    };

    public static getBatchFieldValue(def: BaseFieldDefinition): FormValue<any> | FormValue<any>[] {
        // Always return empty values for lists (MultiValued)
        if (def.ParentDottedKey && def.MultiValued) {
            return this.EmptyValueMapping[def.Type];
        }

        return this.FieldMapping[def.Type].call(this, def);
    }

    private static getBatchSimpleValue(def: BaseFieldDefinition): FormValue<any> {
        return new FormValue(null, FormValueState.INDETERMINATE);
    }

    private static getBatchEnumValue(def: BaseFieldDefinition): FormValue<any>[] {
        // Enum is a single value, but control receives an array of Set and Indeterminate Enum values
        // Return all values as Indeterminate
        return (def.PossibleValues ?? []).map(enumValue => new FormValue(enumValue, FormValueState.INDETERMINATE));
    }

    private static getBatchListValue(def: BaseFieldDefinition): FormValue<any>[] {
        // List values do not require this since we already only add or remove specific values and never overwrite everything
        return [];
    }

    private static getBatchComplexValue(def: BaseFieldDefinition): FormValue<any> {
        return new FormValue({}, FormValueState.INDETERMINATE);
    }

    private static getBatchPositionValue(def: BaseFieldDefinition): FormValue<TypedValue>[] {
        // Position is a single value, but control receives an array of Set and Indeterminate Typed values
        // Return Address and Location as indeterminate
        return [
            new FormValue(
                {
                    type: 'Address',
                    value: ''
                },
                FormValueState.INDETERMINATE
            ),
            new FormValue(
                {
                    type: 'Location',
                    value: ''
                },
                FormValueState.INDETERMINATE
            )
        ];
    }

    private static getBatchThesaurusValue(def: BaseFieldDefinition): FormValue<ThesaurusConceptReference>[] {
        // Thesaurus is a single value, but control receives an array of Set and Indeterminate values
        return [
            new FormValue(
                {
                    Uri: ''
                },
                FormValueState.INDETERMINATE
            )
        ];
    }

    private static getBatchUnsupportedValue(def: BaseFieldDefinition): FormValue<null> {
        console.error(`Batch ${def.Type} field not supported`);
        return new FormValue(null, FormValueState.INDETERMINATE);
    }
}
